import i18n from '@/plugins/vue-i18n.js';
import { toRaw } from 'vue';
import { updateLocalStorage } from '@/store/hooks/useLocalStoreContext.ts';
import * as Sentry from '@sentry/vue';
import { getOnce } from '@/plugins/axios.js';
import { wait } from '@/plugins/vue-wait.ts';
import { useTimedPageAction } from '@/hooks/useTimedPageAction.ts';
import { useUserStore } from '@/store/modules/user/store.js';
import Moment from '@/value-objects/Moment.js';
import TimedPageActions from '@/constants/TimedPageActions.ts';
import { assertEntityImport } from '@/utils/assertEntityImport.ts';
import { getRegionalDisplayName } from '@/utils/translations.ts';

const contextAccountRequests = new WeakMap();

export default {
    beginContextAccountRequest(accountId, reuseMatchingCurrentAccount = false) {
        const requestedAccountId = Number(accountId);
        // Pinia devtools gives each action call a different proxy for the same store.
        const store = toRaw(this);
        const activeRequest = contextAccountRequests.get(store);
        if (reuseMatchingCurrentAccount && activeRequest?.accountId === requestedAccountId) {
            return activeRequest;
        }

        const request = { accountId: requestedAccountId };
        contextAccountRequests.set(store, request);

        return request;
    },

    isCurrentContextAccountRequest(request) {
        return contextAccountRequests.get(toRaw(this)) === request;
    },

    async setContextAccount(accountData, request = null) {
        const activeRequest = request || this.beginContextAccountRequest(accountData?.id);
        const { default: Account } = await import('@/entities/Account.js');
        assertEntityImport(Account, 'Account');

        if (!this.isCurrentContextAccountRequest(activeRequest)) {
            return false;
        }

        this.contextAccount = new Account(accountData);
        this.contextPdfTemplates = [];
        this.contextPdfTemplatesLoaded = false;

        updateLocalStorage({ accountId: this.contextAccount.id });

        return true;
    },

    async setAccounts(accounts) {
        const { default: Account } = await import('@/entities/Account.js');
        assertEntityImport(Account, 'Account');
        this.accounts = Object.fromEntries(
            accounts.map((account) => [account.id, new Account(account)])
        );
    },

    setPdfTemplates(pdfTemplates) {
        this.contextPdfTemplates = pdfTemplates;
        this.contextPdfTemplatesLoaded = true;
    },

    setTemplateAttributes(templateAttributes) {
        this.templateAttributes = Array.isArray(templateAttributes) ? templateAttributes : [];
    },

    setAccountVehicleModels(accountVehicleModels) {
        this.accountVehicleModels = accountVehicleModels || {
            wanted: [],
            exchange: [],
        };
    },

    setCustomCategories(customCategories) {
        this.config.customCategories = customCategories;
    },

    setAutomatableTriggerFields(automatableTriggerFields) {
        this.config.automatableTriggerFields = automatableTriggerFields;
    },

    async fetchContextAccount(id, bypassCache = false, request = null) {
        const activeRequest = request || this.beginContextAccountRequest(id);

        if (!this.isCurrentContextAccountRequest(activeRequest)) {
            return false;
        }

        // Track switch_account only when we already have a context account and change to a new one.
        const currentAccountId = this.contextAccount?.id ?? null;
        const isAccountSwitch = currentAccountId !== null && currentAccountId !== id;
        const timedAction = isAccountSwitch
            ? useTimedPageAction({
                  action: TimedPageActions.SWITCH_ACCOUNT,
                  additionalData: {},
              })
            : null;
        wait.start('fetching.contextAccount');

        const runFetch = async () => {
            const response = await getOnce(
                `v1/context-account/${id}`,
                bypassCache ? { bypassCache: Moment.now().timestamp() } : {}
            );

            if (!response?.data || !this.isCurrentContextAccountRequest(activeRequest)) {
                return false;
            }

            const committed = await this.setContextAccount(response.data, activeRequest);

            if (!committed) {
                return false;
            }

            await Promise.all([
                this.fetchAccountConfigs(id, activeRequest),
                this.fetchTemplateAttributes(id, activeRequest),
                this.fetchAccountVehicleModels(id, activeRequest),
            ]);

            return this.isCurrentContextAccountRequest(activeRequest);
        };

        try {
            if (timedAction) {
                return await timedAction.run(runFetch);
            }

            return await runFetch();
        } catch (error) {
            Sentry.captureException(error);

            return false;
        } finally {
            wait.end('fetching.contextAccount');
        }
    },

    async fetchAccounts(bypassCache = false) {
        wait.start('fetch.accounts');
        try {
            const response = await getOnce(
                'v1/auth-user/accounts',
                bypassCache ? { bypassCache: Moment.now().timestamp() } : {}
            );

            if (response?.data) {
                await this.setAccounts(response.data);
            }
        } catch (error) {
            Sentry.captureException(error);
        } finally {
            wait.end('fetch.accounts');
        }
    },

    async reloadContextAccount(bypassCache = false) {
        const accountId = this.contextAccount?.id;

        if (!accountId) {
            return;
        }

        const request = this.beginContextAccountRequest(accountId);

        const [accountLoaded, users] = await Promise.all([
            this.fetchContextAccount(accountId, bypassCache, request),
            useUserStore().fetchUsersByAccountId(accountId, false, request),
        ]);

        if (accountLoaded && users && this.isCurrentContextAccountRequest(request)) {
            await useUserStore().setContextAccountUsers(users, accountId, request);
        }
    },

    async fetchTemplateAttributes(accountId = null, request = null) {
        const id = accountId || this.contextAccount?.id;
        if (!id) return;

        try {
            const result = await getOnce(`v1/accounts/${id}/attributes`);
            if (!result || (request && !this.isCurrentContextAccountRequest(request))) return;
            const attributes = Array.isArray(result) ? result : result.data;
            this.setTemplateAttributes(attributes || []);
        } catch (error) {
            Sentry.captureException(error);
        }
    },

    async fetchAccountVehicleModels(accountId = null, request = null) {
        const id = accountId || this.contextAccount?.id;
        if (!id) return;

        try {
            const response = await getOnce(`v1/accounts/${id}/vehicle-models`);
            if (!response || (request && !this.isCurrentContextAccountRequest(request))) return;

            if (!response.data) {
                this.setAccountVehicleModels(null);
                return;
            }

            Object.keys(response.data).forEach((type) => {
                response.data[type] = response.data[type].map((model) =>
                    getRegionalDisplayName(model, i18n.getLocale(), null, model.name)
                );
            });

            this.setAccountVehicleModels(response.data);
        } catch (error) {
            Sentry.captureException(error);
        }
    },

    async fetchAccountConfigs(accountId, request = null) {
        if (!accountId) return;

        wait.start('fetching.accountConfigs');
        try {
            const response = await getOnce(`v1/accounts/${accountId}/configs`);
            if (!response || (request && !this.isCurrentContextAccountRequest(request))) return;
            this.setCustomCategories(response.data?.customCategories || []);
            this.setAutomatableTriggerFields(response.data?.automatableTriggerFields || {});
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching account configs:', error);
        } finally {
            wait.end('fetching.accountConfigs');
        }
    },

    async fetchContextPdfTemplates(reload = false) {
        if (!this.contextAccount?.id || (!reload && this.contextPdfTemplatesLoaded)) {
            return;
        }

        const accountId = this.contextAccount.id;
        wait.start('fetching.pdfTemplates');
        try {
            const response = await getOnce(`v1/accounts/${accountId}/pdf-templates`);

            if (response?.data && Number(this.contextAccount?.id) === Number(accountId)) {
                this.setPdfTemplates(response.data);
            }
        } catch (error) {
            Sentry.captureException(error);
        } finally {
            wait.end('fetching.pdfTemplates');
        }
    },
};
